/**
 * Port of the table in `brouter-web-master/js/control/TrackMessages.js`.
 *
 * The engine returns a `messages` array per leg whose first row is the column names and
 * whose remaining rows are one entry per way segment. Rows from all legs are concatenated.
 */

import { BRouterSegment, LatLng } from '../models/brouter-map.model';

/** Abbreviated headings, matching the legacy `columnOptions`. */
const COLUMN_LABELS: Record<string, string> = {
  Elevation: 'elev.',
  Distance: 'dist.',
  CostPerKm: '$/km',
  ElevCost: 'elev$',
  TurnCost: 'turn$',
  NodeCost: 'node$',
  InitialCost: 'initial$'
};

/** Carried in every row so a row can be located on the map, but not shown. */
const HIDDEN_COLUMNS = new Set(['Longitude', 'Latitude']);

/** Coordinates arrive as integer microdegrees. */
const MICRODEGREES = 1_000_000;

export interface TrackMessageColumn {
  prop: string;
  name: string;
}

export interface TrackMessageRow {
  /** Position of this row within the concatenated table, used as a stable identity. */
  index: number;
  values: Record<string, string>;
}

export interface TrackMessageTable {
  columns: TrackMessageColumn[];
  rows: TrackMessageRow[];
}

export const emptyTrackMessageTable: TrackMessageTable = { columns: [], rows: [] };

export const buildTrackMessageTable = (
  segments: readonly (BRouterSegment | null)[]
): TrackMessageTable => {
  let headings: string[] | null = null;
  const rows: TrackMessageRow[] = [];

  for (const segment of segments) {
    const messages = segment?.properties?.messages;
    if (!messages || messages.length < 2) {
      continue;
    }

    headings ??= messages[0].map(String);

    for (const message of messages.slice(1)) {
      const values: Record<string, string> = {};
      headings.forEach((heading, i) => {
        values[heading] = String(message[i] ?? '');
      });
      rows.push({ index: rows.length, values });
    }
  }

  if (!headings) {
    return emptyTrackMessageTable;
  }

  const columns = headings
    .filter(heading => !HIDDEN_COLUMNS.has(heading))
    .map(heading => ({ prop: heading, name: COLUMN_LABELS[heading] ?? heading }));

  return { columns, rows };
};

/** Where a table row sits on the map, or `null` when the row carries no coordinates. */
export const rowToLatLng = (row: TrackMessageRow): LatLng | null => {
  const rawLng = row.values['Longitude']?.trim();
  const rawLat = row.values['Latitude']?.trim();

  // Guarded explicitly because `Number('')` is 0, which would place the row at null island.
  if (!rawLng || !rawLat) {
    return null;
  }

  const lng = Number(rawLng);
  const lat = Number(rawLat);

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return { lat: lat / MICRODEGREES, lng: lng / MICRODEGREES };
};
